import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { getLeaderboard, getDailyLeaderboard, getWeeklyLeaderboard, getCategoryLeaderboard, getMe, getCategories, getLeagueWeekly } from "@/lib/api";
import Header from "@/components/Header";
import ProGateModal from "@/components/ProGateModal";
import { colorForName, initialFromName } from "@/lib/avatar";

const MEDAL = { 1: "🥇", 2: "🥈", 3: "🥉" };
const MEDAL_BG = { 1: "#FFF3B0", 2: "#F0F0F0", 3: "#F5E0C8" };
const MEDAL_BORDER = { 1: "#B8860B", 2: "#aaa", 3: "#A0522D" };

const CAT_COLORS = {
  vida_cotidiana: "#FFDE59", cultura_pop: "#A8FF76", comida: "#FF4C4C",
  deportes: "#7DD3FC", tecnologia: "#C084FC", filosofia: "#FB923C",
  absurdos: "#F9A8D4", argentina_profunda: "#6EE7B7",
  conspiranoia: "#FCD34D", dilemas_morales: "#F87171",
};

const TABS = [
  { id: "league",  label: "🏅 Ligas",      color: "#FFD700", fn: null },
  { id: "alltime", label: "All-time",      color: "#FFDE59", fn: getLeaderboard },
  { id: "weekly",  label: "Esta semana",   color: "#A8FF76", fn: getWeeklyLeaderboard },
  { id: "daily",   label: "🔥 Reto del día", color: "#FF4C4C", textWhite: true, fn: getDailyLeaderboard },
];

const LEAGUE_TIERS = [
  { id: "bronce", label: "Bronce", emoji: "🥉", color: "#CD7F32" },
  { id: "plata", label: "Plata", emoji: "🥈", color: "#C0C0C0" },
  { id: "oro", label: "Oro", emoji: "🥇", color: "#FFD700" },
];

function Avatar({ entry }) {
  if (entry.picture) {
    return <img src={entry.picture} alt={entry.name} referrerPolicy="no-referrer"
      className="w-10 h-10 md:w-12 md:h-12 rounded-full border-2 border-[#101010] shrink-0" />;
  }
  const color = colorForName(entry.name);
  const initial = initialFromName(entry.name);
  return (
    <span className="w-10 h-10 md:w-12 md:h-12 rounded-full border-2 border-[#101010] flex items-center justify-center font-display text-lg shrink-0"
      style={{ background: color }}>
      {initial}
    </span>
  );
}

function LeaderboardRow({ entry, index, tab, selectedCat }) {
  const isTop3 = entry.rank <= 3;
  const bg = entry.is_me
    ? "#FFDE59"
    : isTop3 ? MEDAL_BG[entry.rank]
    : index % 2 === 0 ? "white" : "#F0EDE1";

  return (
    <motion.div
      initial={{ x: -16, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ delay: Math.min(index * 0.03, 0.4), duration: 0.28 }}
      className="flex items-center gap-3 md:gap-4 px-4 py-3 md:py-4 border-b-4 border-[#101010] last:border-b-0"
      style={{
        background: bg,
        borderLeft: isTop3 ? `6px solid ${MEDAL_BORDER[entry.rank]}` : undefined,
      }}
      data-testid={`leaderboard-row-${entry.rank}`}
    >
      {/* Rank */}
      <div className="w-9 md:w-11 text-center shrink-0">
        {isTop3
          ? <span className="text-2xl md:text-3xl leading-none">{MEDAL[entry.rank]}</span>
          : <span className="font-mono-cm font-bold text-lg md:text-xl tabular-nums text-[#101010]/50">
              {entry.rank}
            </span>
        }
      </div>

      <Avatar entry={entry} />

      <div className="flex-1 min-w-0">
        {/* Name + badges */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-display uppercase text-sm md:text-base leading-tight truncate">
            {entry.name}
          </span>
          {entry.is_me && (
            <span className="font-mono-cm text-[9px] uppercase tracking-widest bg-[#101010] text-[#FFDE59] px-1.5 py-0.5 rounded shrink-0">
              Vos
            </span>
          )}
          {entry.is_pro && (
            <span className="font-mono-cm text-[9px] uppercase tracking-widest bg-[#FFDE59] border border-[#101010] px-1.5 py-0.5 rounded shrink-0"
              data-testid={`pro-badge-${entry.rank}`}>
              PRO
            </span>
          )}
          {entry.favorite_title_icon && entry.favorite_title_label && (
            <span className="font-mono-cm text-[9px] uppercase tracking-widest bg-[#101010] text-white px-1.5 py-0.5 rounded shrink-0">
              {entry.favorite_title_icon} {entry.favorite_title_label}
            </span>
          )}
        </div>
        {/* Category + stance */}
        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
          {entry.category_label && (
            <span className="font-mono-cm text-[9px] uppercase tracking-widest bg-[#101010]/10 px-1.5 py-0.5 rounded shrink-0">
              {entry.category_label}
            </span>
          )}
          <span className="font-mono-cm text-[10px] text-[#101010]/45 italic truncate">
            &ldquo;{entry.stance}&rdquo;
          </span>
        </div>
      </div>

      {/* Score */}
      <div className="text-right shrink-0">
        <div className="font-display text-2xl md:text-3xl tabular-nums">
          {entry.score.toLocaleString("es-AR")}
        </div>
        {entry.games_played != null && entry.games_played > 0 && (
          <div className="font-mono-cm text-[9px] uppercase tracking-widest text-[#101010]/40">
            {entry.games_played} debate{entry.games_played !== 1 ? "s" : ""}
          </div>
        )}
        {entry.best_score != null && tab !== "daily" && !selectedCat && (
          <div className="font-mono-cm text-[9px] uppercase tracking-widest text-[#101010]/40">
            mejor: {entry.best_score}
          </div>
        )}
      </div>
    </motion.div>
  );
}

function LeagueRow({ entry, index }) {
  const zoneBg =
    entry.zone === "promote" ? "#A8FF76"
    : entry.zone === "demote" ? "#FF4C4C33"
    : entry.is_me ? "#FFDE59"
    : index % 2 === 0 ? "white" : "#F0EDE1";

  return (
    <motion.div
      initial={{ x: -16, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ delay: Math.min(index * 0.03, 0.4), duration: 0.28 }}
      className="flex items-center gap-3 md:gap-4 px-4 py-3 md:py-4 border-b-4 border-[#101010] last:border-b-0"
      style={{
        background: zoneBg,
        borderLeft: entry.zone === "promote" ? "6px solid #2d7a00" : entry.zone === "demote" ? "6px solid #FF4C4C" : undefined,
      }}
      data-testid={`league-row-${entry.rank}`}
    >
      <div className="w-9 md:w-11 text-center shrink-0 font-mono-cm font-bold text-lg tabular-nums">
        {entry.rank}
      </div>
      <Avatar entry={entry} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-display uppercase text-sm md:text-base truncate">{entry.name}</span>
          {entry.is_me && (
            <span className="font-mono-cm text-[9px] uppercase tracking-widest bg-[#101010] text-[#FFDE59] px-1.5 py-0.5 rounded">Vos</span>
          )}
          {entry.zone === "promote" && (
            <span className="font-mono-cm text-[9px] uppercase tracking-widest bg-[#2d7a00] text-white px-1.5 py-0.5 rounded">↑ Sube</span>
          )}
          {entry.zone === "demote" && (
            <span className="font-mono-cm text-[9px] uppercase tracking-widest bg-[#FF4C4C] text-white px-1.5 py-0.5 rounded">↓ Baja</span>
          )}
        </div>
        <div className="font-mono-cm text-[10px] text-[#101010]/45 uppercase tracking-widest">
          {entry.games_played} partida{entry.games_played !== 1 ? "s" : ""} esta semana
        </div>
      </div>
      <div className="font-display text-2xl md:text-3xl tabular-nums shrink-0">{entry.score}</div>
    </motion.div>
  );
}

export default function LeaderboardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [user, setUser]             = useState(null);
  const [categories, setCategories] = useState([]);
  const [tab, setTab]               = useState(location.state?.tab || "league");
  const [selectedCat, setSelectedCat] = useState(null);
  const [pendingCat, setPendingCat]   = useState(null);
  const [gateOpen, setGateOpen]     = useState(false);
  const [leagueTier, setLeagueTier] = useState("bronce");
  const [leagueData, setLeagueData] = useState(null);

  // Load user + categories once
  useEffect(() => {
    Promise.all([
      getMe().catch(() => null),
      getCategories().catch(() => []),
    ]).then(([me, cats]) => {
      setUser(me);
      setCategories(cats);
      if (me?.league_tier) setLeagueTier(me.league_tier);
    });
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      if (tab === "league") {
        const data = await getLeagueWeekly(leagueTier);
        setLeagueData(data);
        setItems([]);
        return;
      }
      setLeagueData(null);
      const tabDef = TABS.find((t) => t.id === tab);
      const data = selectedCat
        ? await getCategoryLeaderboard(selectedCat)
        : await tabDef.fn();
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab, selectedCat, leagueTier]);

  const onUpgraded = async () => {
    setGateOpen(false);
    const me = await getMe().catch(() => null);
    setUser(me);
    if (pendingCat) {
      setSelectedCat(pendingCat);
      setPendingCat(null);
    } else {
      load();
    }
  };

  const handleCatClick = (catId) => {
    if (selectedCat === catId) {
      setSelectedCat(null); // deselect
      return;
    }
    if (!user?.is_pro) {
      setPendingCat(catId);
      setGateOpen(true);
      return;
    }
    setSelectedCat(catId);
  };

  // Split: main list vs "my rank below top"
  const mainList     = items.filter((e) => !(e.is_me && e.rank > 20));
  const myRankEntry  = items.find((e) => e.is_me && e.rank > 20);
  const activeTab    = TABS.find((t) => t.id === tab);
  const activeCatLabel = selectedCat
    ? categories.find((c) => c.id === selectedCat)?.label
    : null;

  return (
    <div className="min-h-screen px-4 sm:px-6 py-6 md:py-10">
      {user
        ? <Header user={user} current="leaderboard" />
        : (
          <div className="max-w-3xl mx-auto flex items-center justify-between mb-6">
            <button onClick={() => navigate("/")} data-testid="home-link"
              className="font-display text-xl md:text-2xl uppercase tracking-tighter">
              Conven<span className="text-[#FF4C4C]">ce</span>me
            </button>
            <Link to="/" data-testid="cta-play"
              className="cm-press text-xs font-mono-cm uppercase tracking-widest border-2 border-[#101010] bg-[#FFDE59] px-3 py-1.5 rounded-md cm-shadow-sm">
              Jugar
            </Link>
          </div>
        )
      }

      <div className="max-w-3xl mx-auto space-y-4">
        <h1 className="font-display text-4xl md:text-6xl uppercase">
          Leader<span className="text-[#FF4C4C]">board</span>
        </h1>
        {tab === "league" && !selectedCat && (
          <p className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/50 -mt-2">
            Top {leagueData?.promote_count || 10} sube · Bottom {leagueData?.demote_count || 5} baja · reset semanal
          </p>
        )}
        {tab !== "daily" && tab !== "league" && !selectedCat && (
          <p className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/50 -mt-2">
            Puntos acumulados — cuanto más jugás y mejor argumentás, más subís
          </p>
        )}

        {/* Tabs de tiempo */}
        <div className="flex items-center gap-2 flex-wrap" role="tablist" data-testid="leaderboard-tabs">
          {TABS.map((t) => {
            const active = tab === t.id && !selectedCat;
            return (
              <button key={t.id} role="tab" data-testid={`leaderboard-tab-${t.id}`}
                onClick={() => { setTab(t.id); setSelectedCat(null); }}
                className={`cm-press border-4 border-[#101010] rounded-xl px-4 py-2 font-display uppercase text-sm md:text-base cm-shadow-sm transition-colors ${
                  active ? (t.textWhite ? "text-white" : "") : "bg-white text-[#101010]/50"
                }`}
                style={active ? { background: t.color } : {}}>
                {t.label}
              </button>
            );
          })}
        </div>

        {tab === "league" && (
          <div className="space-y-3" data-testid="league-panel">
            <div className="flex items-center gap-2 flex-wrap">
              {LEAGUE_TIERS.map((t) => {
                const active = leagueTier === t.id;
                const isMine = user?.league_tier === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setLeagueTier(t.id)}
                    data-testid={`league-tier-${t.id}`}
                    className={`cm-press border-4 border-[#101010] rounded-xl px-4 py-2 font-display uppercase text-sm cm-shadow-sm ${
                      active ? "" : "bg-white text-[#101010]/50"
                    }`}
                    style={active ? { background: t.color } : {}}
                  >
                    {t.emoji} {t.label}{isMine ? " · vos" : ""}
                  </button>
                );
              })}
            </div>
            {leagueData && (
              <div className="bg-[#101010] text-white border-4 border-[#101010] rounded-xl px-4 py-3 flex flex-wrap items-center gap-3 justify-between">
                <div>
                  <div className="font-mono-cm text-[10px] uppercase tracking-widest text-white/60">
                    Semana {leagueData.week_id} · Liga {leagueData.tier_label}
                  </div>
                  <div className="font-display uppercase text-lg">
                    {leagueData.my_rank
                      ? `Vos: #${leagueData.my_rank} · ${leagueData.my_score} pts`
                      : leagueData.my_score > 0
                      ? `${leagueData.my_score} pts · sin ranking aún`
                      : "Jugá para entrar al ranking"}
                  </div>
                </div>
                <div className="font-mono-cm text-[10px] uppercase tracking-widest text-[#FFDE59]">
                  {leagueData.days_left} día{leagueData.days_left !== 1 ? "s" : ""} restantes · {leagueData.players_active} activos
                </div>
              </div>
            )}
          </div>
        )}

        {/* Filtro por categoría */}
        {categories.length > 0 && tab !== "league" && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/50">
                Por categoría
              </span>
              {!user?.is_pro && (
                <span className="font-mono-cm text-[9px] uppercase tracking-widest bg-[#FF4C4C] text-white px-1.5 py-0.5 rounded border border-[#101010]">
                  PRO
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedCat && (
                <button
                  onClick={() => setSelectedCat(null)}
                  className="cm-press shrink-0 border-4 border-[#101010] rounded-xl px-3 py-1.5 font-display uppercase text-sm bg-[#101010] text-white cm-shadow-sm"
                >
                  ✕ Todos
                </button>
              )}
              {categories.map((c) => {
                const active = selectedCat === c.id;
                const color = CAT_COLORS[c.id] || "#FFDE59";
                return (
                  <button
                    key={c.id}
                    onClick={() => handleCatClick(c.id)}
                    className="cm-press shrink-0 border-4 border-[#101010] rounded-xl px-3 py-1.5 font-display uppercase text-sm cm-shadow-sm"
                    style={{ background: active ? color : "#F0EDE1" }}
                    data-testid={`cat-filter-${c.id}`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Subtítulo contextual */}
        {activeCatLabel && (
          <div className="font-mono-cm text-xs uppercase tracking-widest text-[#101010]/60">
            Mejores en <strong className="text-[#101010]">{activeCatLabel}</strong> — all-time
          </div>
        )}

        {/* Lista principal */}
        <div className="bg-white border-4 border-[#101010] rounded-xl cm-shadow overflow-hidden" data-testid="leaderboard-list">
          <AnimatePresence mode="wait">
            {loading ? (
              <div key="loading" className="p-6 font-mono-cm uppercase tracking-widest text-sm">
                Cargando...
              </div>
            ) : tab === "league" ? (
              leagueData?.entries?.length ? (
                <div key={`league-${leagueTier}`}>
                  {leagueData.entries.map((entry, i) => (
                    <LeagueRow key={`${entry.rank}-${entry.name}`} entry={entry} index={i} />
                  ))}
                </div>
              ) : (
                <div key="league-empty" className="p-8 text-center" data-testid="league-empty">
                  <div className="font-display uppercase text-2xl mb-2">Nadie sumó puntos aún</div>
                  <div className="text-sm font-mono-cm uppercase tracking-widest text-[#101010]/60">
                    Sé el primero en la liga {leagueData?.tier_label || leagueTier} esta semana
                  </div>
                </div>
              )
            ) : mainList.length === 0 ? (
              <div key="empty" className="p-8 text-center" data-testid="leaderboard-empty">
                <div className="font-display uppercase text-2xl mb-2">Sin debates aún</div>
                <div className="text-sm font-mono-cm uppercase tracking-widest text-[#101010]/60">
                  {tab === "daily" ? "Nadie jugó el reto de hoy" : activeCatLabel ? `Nadie jugó ${activeCatLabel} todavía` : "Sé el primero en jugar"}
                </div>
              </div>
            ) : (
              <div key={`${tab}-${selectedCat}`}>
                {mainList.map((entry, i) => (
                  <LeaderboardRow key={`${entry.rank}-${entry.name}`} entry={entry} index={i} tab={tab} selectedCat={selectedCat} />
                ))}
              </div>
            )}
          </AnimatePresence>
        </div>

        {/* Tu posición (debajo del top) */}
        {myRankEntry && tab !== "league" && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-3 mb-2">
              <div className="flex-1 h-0.5 bg-[#101010]/15" />
              <span className="font-mono-cm text-[11px] uppercase tracking-widest text-[#101010]/40">Tu posición</span>
              <div className="flex-1 h-0.5 bg-[#101010]/15" />
            </div>
            <div className="bg-[#FFDE59] border-4 border-[#101010] rounded-xl cm-shadow overflow-hidden">
              <LeaderboardRow entry={myRankEntry} index={0} tab={tab} selectedCat={selectedCat} />
            </div>
            <p className="text-center font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/40 mt-2">
              Jugá más para subir en el ranking
            </p>
          </motion.div>
        )}

        {/* PRO upsell — categorías */}
        {user && !user.is_pro && (
          <div className="border-4 border-[#101010] rounded-xl cm-shadow bg-[#101010] p-4 flex items-center gap-3">
            <span className="text-2xl">🏅</span>
            <div className="flex-1 min-w-0">
              <div className="font-mono-cm text-[11px] uppercase tracking-widest text-white/70">
                PRO — ranking por categoría
              </div>
              <div className="font-mono-cm text-[10px] uppercase tracking-widest text-white/40">
                ¿Sos el mejor en Conspiranoia? ¿En Dilemas Morales? Descubrilo.
              </div>
            </div>
            <button onClick={() => setGateOpen(true)}
              className="cm-press shrink-0 bg-[#FFDE59] border-2 border-[#FFDE59] text-[#101010] font-display uppercase text-sm rounded-xl px-4 py-2 cm-shadow-sm">
              Desbloquear
            </button>
          </div>
        )}
      </div>

      <ProGateModal
        open={gateOpen}
        onClose={() => { setGateOpen(false); setPendingCat(null); }}
        onUpgraded={onUpgraded}
        reason="leaderboard"
      />
    </div>
  );
}
